import { motion, AnimatePresence } from "framer-motion";
import { Trash2, X, Loader2, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import { permanentlyDeleteDeal } from "../../requests/Uploaded_Deals/UploadedDealsService";

const PermanentDeleteModal = ({ isOpen, onClose, deal, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setIsSubmitting(false);
  }, [isOpen, deal?.deal_slug]);

  const handleDelete = async () => {
    if (!deal?.deal_slug) return;
    setIsSubmitting(true);
    try {
      await permanentlyDeleteDeal(deal.deal_slug, {
        onSuccess: () => {
          onSuccess && onSuccess();
          onClose();
        },
        onError: () => {
          setIsSubmitting(false);
        },
      });
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed inset-0 z-[1000] flex w-full items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md min-w-0 rounded-2xl bg-white shadow-[0_25px_60px_-12px_rgba(0,0,0,0.35)] overflow-hidden box-border"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="perma-delete-modal-title"
            >
              {/* Top accent stripe */}
              <div className="h-1 bg-gradient-to-r from-red-500 via-rose-500 to-pink-500" />

              {/* Header */}
              <div className="px-6 pt-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-red-50 border border-red-100">
                      <Trash2 className="w-5 h-5 text-red-600" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 overflow-hidden">
                      <h2
                        id="perma-delete-modal-title"
                        className="text-lg font-bold text-gray-900 leading-tight break-words"
                      >
                        Permanently Delete
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5 break-words">
                        This action cannot be undone
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Deal name card */}
              <div className="px-6 pb-4 min-w-0 overflow-hidden">
                <div className="flex items-start gap-3 px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200/80 min-w-0 overflow-hidden">
                  <div className="w-1 self-stretch shrink-0 rounded-full bg-red-400" />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                      Deal to Delete
                    </p>
                    <p className="text-sm font-semibold text-gray-900 break-words whitespace-normal leading-snug w-full">
                      {deal?.title ?? `#${deal?.id ?? "—"}`}
                    </p>
                    {deal?.company_name && (
                      <p className="text-xs text-gray-500 mt-0.5 break-words whitespace-normal w-full">
                        {deal.company_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning notice */}
              <div className="px-6 pb-5 min-w-0 overflow-hidden">
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 rounded-xl border border-red-100 min-w-0 overflow-hidden">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 leading-relaxed break-words whitespace-normal min-w-0 w-full">
                    <strong>Warning:</strong> This will <strong>permanently remove</strong> this deal
                    and all associated data from the system. There is no way to recover it after deletion.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 pb-6 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm shadow-red-200 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Forever
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PermanentDeleteModal;
